/**
 * DEFUSE S15 — typed property descriptors for Text / MText. Special fields only.
 *
 * `Height`/`Width` for MTEXT map to `nominalHeight` (group 40, `.positive()`) /
 * `referenceWidth` (group 41, `.nonnegative()`) — NOT a `height`/`width` field
 * (the MTEXT schema has neither); this is the exact seam `numericFieldClassifi-
 * cation.labelToFieldByType.MText` encodes. `Justify` is a decoded label
 * (Appendix B pureUiKeys), read-only from `hJustification`/`vJustification`.
 */

import type { MtextEntity, TextEntity } from "@dxf-ai-editor/drawing-model";

import { justifyLabel } from "../previewBuilders";
import {
  calc,
  coord,
  enumField,
  make,
  num,
  styleRef,
  txt,
  type TypedPropertyDescriptor,
} from "./types";

export const textDescriptors: TypedPropertyDescriptor[] = [
  txt<TextEntity>("Contents", "contents", (e) => e.contents),
  styleRef<TextEntity>("Text style", "styleName", (e) => e.styleName),
  num<TextEntity>("Height", "height", (e) => e.height, { min: 0 }),
  num<TextEntity>("Rotation", "rotation", (e) => e.rotation),
  num<TextEntity>("Width factor", "widthFactor", (e) => e.widthFactor),
  num<TextEntity>("Oblique angle", "obliqueAngle", (e) => e.obliqueAngle),
  // decoded label ("Baseline Left") from the (h,v) justification codes via the
  // AutoCAD-parity justifyLabel(h, v) — the reconcile target the P2 finding
  // cites. NOTE a pre-existing DIVERGENCE: the DRAFT path
  // (deriveTextPreviewProperties) uses this two-word label, while the imported
  // single-select path (dxfGeometry.buildTextPreview → justifyFromCodes) shows a
  // one-word UPPERCASE label ("CENTER"). S16 unifies both to justifyLabel here.
  // read exposes the raw code pair for reference.
  calc<TextEntity>(
    "Justify",
    (e) => [e.hJustification, e.vJustification],
    (e) => justifyLabel(e.hJustification, e.vJustification),
  ),
  // GRIPS-DRAG-MORE-TYPES (Codex P2, round 2) — Text's insertion is a
  // whole-entity TRANSLATE, so moving it must ALSO shift `alignmentPoint`
  // (when the TEXT is justified) by the same delta — mirroring every other
  // whole-entity transform (`geometryTransforms.ts::transformEntityCoords`).
  // Routed through the `textInsertionMoveMutator` SIDE-EFFECT
  // (`sideEffectMutators.ts`) rather than a plain field set: this is
  // DELIBERATELY the coupling site instead of making "Alignment X"/
  // "Alignment Y" themselves independently owned — see
  // `EXTENDED_GEOMETRY_OPTIONAL_BASE_POINT_COORDS`'s doc (typedPropertyWriter.ts)
  // for why that alternative was tried and reverted (a shell↔document
  // divergence class the shell's bag-only reject mirror cannot close).
  coord<TextEntity>("Insertion X", "insertionPoint.x", (e) => e.insertionPoint.x, {
    sideEffects: "textInsertionMoveMutator",
  }),
  coord<TextEntity>("Insertion Y", "insertionPoint.y", (e) => e.insertionPoint.y, {
    sideEffects: "textInsertionMoveMutator",
  }),
  // "Alignment X"/"Alignment Y" stay a documented silent no-op when edited
  // directly (excluded from the typed-writer owned set — an OPTIONAL base
  // point that can be ABSENT; see the exclusion-set doc above) — unchanged
  // from before this slice. Their display still falls back to insertionPoint
  // for a default-justified TEXT (no `alignmentPoint`).
  coord<TextEntity>(
    "Alignment X",
    "alignmentPoint.x",
    (e) => e.alignmentPoint?.x ?? e.insertionPoint.x,
  ),
  coord<TextEntity>(
    "Alignment Y",
    "alignmentPoint.y",
    (e) => e.alignmentPoint?.y ?? e.insertionPoint.y,
  ),
];

export const mtextDescriptors: TypedPropertyDescriptor[] = [
  txt<MtextEntity>("Contents", "contents", (e) => e.contents),
  styleRef<MtextEntity>("Text style", "styleName", (e) => e.styleName),
  num<MtextEntity>("Height", "nominalHeight", (e) => e.nominalHeight, { exclusiveMin: 0 }),
  num<MtextEntity>("Width", "referenceWidth", (e) => e.referenceWidth, { min: 0 }),
  enumField<MtextEntity>("Attachment", "attachmentPoint", (e) => e.attachmentPoint),
  enumField<MtextEntity>("Direction", "drawingDirection", (e) => e.drawingDirection),
  num<MtextEntity>("Rotation", "rotation", (e) => e.rotation),
  num<MtextEntity>("Line spacing", "lineSpacingFactor", (e) => e.lineSpacingFactor),
  // production renders Background fill as On/Off (NOT Yes/No).
  make(
    "Background fill",
    "boolean",
    { path: "backgroundFill", read: (e) => (e as MtextEntity).backgroundFill },
    {
      display: (e) => ((e as MtextEntity).backgroundFill ? "On" : "Off"),
      toRaw: (s) => s.trim().toLowerCase() === "on",
    },
  ),
  coord<MtextEntity>("Insertion X", "insertionPoint.x", (e) => e.insertionPoint.x),
  coord<MtextEntity>("Insertion Y", "insertionPoint.y", (e) => e.insertionPoint.y),
];
